import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Copy, Rocket, AlertCircle, CheckCircle2, Wallet, Network as NetworkIcon, Server, HelpCircle, Download, ExternalLink, ChevronRight, ChevronDown } from 'lucide-react';
import { useNotification } from '@/lib/NotificationContext';
import { EXTERNAL_LINKS, getNetworkConfig } from '@/lib/constants';
import type { Chain, Network } from '@/lib/types';
import { getDeployPath, type DeployPath } from '@/lib/deployPaths';

interface GitHubRelease {
  tag_name: string;
  name: string;
  prerelease: boolean;
  published_at: string;
}

type DetectedOS = 'macos' | 'windows' | 'linux' | 'unknown';

interface BootstrapProps {
  /** Controlled open state from onboarding */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Align setup instructions with the onboarding CTA path */
  deployPath?: DeployPath;
}

export default function Bootstrap({
  open: controlledOpen,
  onOpenChange,
  deployPath = 'release',
}: BootstrapProps) {
  const pathMeta = getDeployPath(deployPath);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const [chain, setChain] = useState<Chain>('base');
  const [network, setNetwork] = useState<Network>('mainnet');
  const [walletPrivateKey, setWalletPrivateKey] = useState('');
  const [webPublicUrl, setWebPublicUrl] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [proxyPort, setProxyPort] = useState('3333');
  const [apiPort, setApiPort] = useState('8082');
  const [ethNodeAddress, setEthNodeAddress] = useState('');
  const [showEnvFile, setShowEnvFile] = useState(false);
  const [showPrereqs, setShowPrereqs] = useState(true);
  const [binaryPlatform, setBinaryPlatform] = useState('linux-x86_64');
  const [selectedVersion, setSelectedVersion] = useState('v5.6.0');
  const [availableReleases, setAvailableReleases] = useState<GitHubRelease[]>([]);
  const [isLoadingReleases, setIsLoadingReleases] = useState(false);
  const [detectedOS, setDetectedOS] = useState<DetectedOS>('unknown');
  const [isEnvExpanded, setIsEnvExpanded] = useState(false);
  const [editableEnvContent, setEditableEnvContent] = useState('');
  const [setupTab, setSetupTab] = useState(pathMeta.bootstrapTab);

  useEffect(() => {
    setSetupTab(pathMeta.bootstrapTab);
  }, [pathMeta.bootstrapTab]);

  useEffect(() => {
    if (isOpen) {
      fetchReleases();
      detectOS();
      // SecretVM / container users often skip the long prereq checklist
      if (deployPath === 'secretvm' || deployPath === 'container') {
        setShowPrereqs(false);
      }
    }
  }, [isOpen, deployPath]);

  const detectOS = async () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = window.navigator.platform.toLowerCase();
    
    let os: DetectedOS = 'unknown';
    let platformKey = 'linux-x86_64';
    
    if (platform.includes('mac') || userAgent.includes('macintosh')) {
      os = 'macos';
      
      // Detect Apple Silicon vs Intel
      let isAppleSilicon = false;
      
      // Check user agent for ARM indicators
      if (userAgent.includes('arm') || platform.includes('arm')) {
        isAppleSilicon = true;
      }
      
      // Check WebGL renderer (most reliable for Apple Silicon detection)
      if (platform === 'macintel' || platform.includes('mac')) {
        try {
          const canvas = document.createElement('canvas');
          const gl = canvas.getContext('webgl');
          if (gl) {
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
              const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
              if (renderer.includes('apple') && (renderer.includes('m1') || renderer.includes('m2') || renderer.includes('m3') || renderer.includes('m4'))) {
                isAppleSilicon = true;
              }
            }
          }
        } catch (e) {
          // Ignore errors
        }
      }
      
      platformKey = isAppleSilicon ? 'darwin-aarch64' : 'darwin-x86_64';
    } else if (platform.includes('win') || userAgent.includes('windows')) {
      os = 'windows';
      platformKey = 'windows-x86_64';
    } else if (platform.includes('linux') || userAgent.includes('linux')) {
      os = 'linux';
      platformKey = 'linux-x86_64';
    }
    
    console.log('🔍 Detected:', os, '→', platformKey);
    
    setDetectedOS(os);
    setBinaryPlatform(platformKey);
  };

  const fetchReleases = async () => {
    setIsLoadingReleases(true);
    try {
      const response = await fetch(
        'https://api.github.com/repos/MorpheusAIs/Morpheus-Lumerin-Node/releases?per_page=30'
      );
      const releases: GitHubRelease[] = await response.json();
      
      // Get latest stable release (no prerelease flag, no suffix)
      const latestStable = releases.find(r => !r.prerelease && !r.tag_name.includes('-'));
      
      // Get latest test release (has -test suffix)
      const latestTest = releases.find(r => r.tag_name.includes('-test'));
      
      // Order: Latest Stable first, then Latest Test
      const filteredReleases: GitHubRelease[] = [];
      if (latestStable) filteredReleases.push(latestStable);
      if (latestTest) filteredReleases.push(latestTest);
      
      setAvailableReleases(filteredReleases);
      
      // Set the latest stable as default
      if (latestStable) {
        setSelectedVersion(latestStable.tag_name);
      }
    } catch (err) {
      console.error('Failed to fetch releases:', err);
      // Fallback to hardcoded version
      setAvailableReleases([{ 
        tag_name: 'v5.6.0', 
        name: 'v5.6.0',
        prerelease: false,
        published_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoadingReleases(false);
    }
  };
  
  const getBinaryDownloadUrl = (platform: string, version: string) => {
    const versionNum = version.startsWith('v') ? version.substring(1) : version;
    // Map platform keys to actual release filename format
    const platformMap: Record<string, string> = {
      'darwin-aarch64': 'mac-arm64',
      'darwin-x86_64': 'mac-x64',
      'linux-x86_64': 'linux-x86_64',
      'windows-x86_64': 'win-x64',
      'mac-arm64': 'mac-arm64',
      'mac-x64': 'mac-x64',
      'win-x64': 'win-x64'
    };
    const mappedPlatform = platformMap[platform] || platform;
    const extension = mappedPlatform.includes('win') ? '.exe' : '';
    return `https://github.com/MorpheusAIs/Morpheus-Lumerin-Node/releases/download/${version}/${mappedPlatform}-morpheus-router-${versionNum}${extension}`;
  };

  const getPlatformName = (platform: string) => {
    const names: Record<string, string> = {
      'linux-x86_64': 'Linux (x86_64)',
      'darwin-x86_64': 'macOS (Intel)',
      'darwin-aarch64': 'macOS (Apple Silicon)',
      'windows-x86_64': 'Windows (x64)',
      'mac-x64': 'macOS (Intel)',
      'mac-arm64': 'macOS (Apple Silicon)',
      'win-x64': 'Windows (x64)'
    };
    return names[platform] || platform;
  };

  const { success, error: showError } = useNotification();

  const generateEnvFile = () => {
    // Allow generation even with empty fields - they'll get placeholders
    setEditableEnvContent(getEnvFileContent());
    setShowEnvFile(true);
  };

  const handleCopyEnv = () => {
    navigator.clipboard.writeText(editableEnvContent);
    success('Copied!', 'ENV file content copied to clipboard');
  };

  const handleDownloadEnv = () => {
    const blob = new Blob([editableEnvContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '.env';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    success('Downloaded!', '.env file downloaded successfully');
  };

  const handleDownloadBinary = () => {
    const url = getBinaryDownloadUrl(binaryPlatform, selectedVersion);
    const a = document.createElement('a');
    a.href = url;
    a.download = detectedOS === 'windows' ? 'proxy-router.exe' : 'proxy-router';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    success('Download started!', 'Binary download initiated');
  };

  const getEnvFileContent = (): string => {
    const networkConfig = getNetworkConfig(chain, network);
    const rpcLine = ethNodeAddress.trim()
      ? `ETH_NODE_ADDRESS=${ethNodeAddress.trim()}`
      : `# ETH_NODE_ADDRESS=https://base-mainnet.g.alchemy.com/v2/<YOUR_KEY>  # required for real providers`;

    if (deployPath === 'secretvm') {
      return `WALLET_PRIVATE_KEY=${walletPrivateKey.trim() || '<FILL_ME_IN_YOUR_PRIVATE_KEY>'}
ETH_NODE_ADDRESS=${ethNodeAddress.trim() || 'https://base-mainnet.g.alchemy.com/v2/<YOUR_KEY>'}
WEB_PUBLIC_URL=${webPublicUrl.trim() || 'https://your-secretvm-hostname'}
COOKIE_CONTENT=admin:${adminPassword.trim() || '<FILL_ME_IN_YOUR_ADMIN_PASSWORD>'}
MODELS_CONFIG_CONTENT={"models":[{"modelId":"0xYOUR_MODEL_ID","modelName":"your-model","apiType":"openai","apiUrl":"http://your-model:8080/v1/chat/completions","concurrentSlots":6,"capacityPolicy":"simple"}]}`;
    }

    return `# Morpheus Proxy Router Configuration
# Deploy path: ${pathMeta.title}
# Chain: ${chain === 'arbitrum' ? 'Arbitrum' : 'Base'}
# Network: ${network === 'mainnet' ? 'Mainnet' : 'Testnet'}
# Generated in-browser only — not stored on any server.

# Blockchain Configuration
ETH_NODE_CHAIN_ID=${networkConfig.chainId}
BLOCKSCOUT_API_URL=${networkConfig.blockscoutApiUrl}
DIAMOND_CONTRACT_ADDRESS=${networkConfig.diamondContract}
MOR_TOKEN_ADDRESS=${networkConfig.morTokenContract}
${rpcLine}

# Wallet Configuration
WALLET_PRIVATE_KEY=${walletPrivateKey.trim() || '<FILL_ME_IN_YOUR_PRIVATE_KEY>'}

# Web Configuration
WEB_PUBLIC_URL=${webPublicUrl.trim() || '<FILL_ME_IN_YOUR_PUBLIC_URL>'}
WEB_ADDRESS=0.0.0.0:${apiPort || '8082'}

# Proxy Configuration
PROXY_ADDRESS=0.0.0.0:${proxyPort || '3333'}

# Logging Configuration
LOG_IS_PROD=false
LOG_LEVEL_APP=info
LOG_JSON=true
LOG_COLOR=true

# Environment
ENVIRONMENT=production

# Admin Authentication (username is always 'admin')
COOKIE_CONTENT=admin:${adminPassword.trim() || '<FILL_ME_IN_YOUR_ADMIN_PASSWORD>'}

# After looking up a model on active.mor.org and bidding, paste MODELS_CONFIG_CONTENT
# from onboarding / Model Configuration Sync (prefer bidding on an existing model Id).
# MODELS_CONFIG_CONTENT='{"models":[...]}'
`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Card className="border-primary/40 bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Rocket className="h-6 w-6 text-primary" />
                <div>
                  <CardTitle className="text-primary">Bootstrap your node</CardTitle>
                  <CardDescription className="text-gray-300">
                    Path: <span className="text-foreground font-medium">{pathMeta.title}</span> — generate
                    ENV / secrets. Session only; nothing is stored on a server.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </DialogTrigger>
      )}

      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5" />
            Bootstrap — {pathMeta.title}
          </DialogTitle>
          <DialogDescription>
            {pathMeta.blurb}. Private keys stay in this browser session only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {!showPrereqs && !showEnvFile && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-yellow-400">Configuration Guide</p>
                  <div className="text-muted-foreground space-y-2">
                    <p>
                      Complete the form below and click <span className="font-medium text-foreground">Next</span> to generate your ENV file and review startup instructions.
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><span className="font-medium text-foreground">Select your blockchain network</span> - Choose the chain and network type (mainnet or testnet)</li>
                      <li><span className="font-medium text-foreground">Enter your credentials</span> - Provide your wallet private key and admin password</li>
                      <li><span className="font-medium text-foreground">Configure network ports</span> - Set the ports for proxy routing and API management</li>
                      <li><span className="font-medium text-foreground">Specify your public URL</span> - Enter how your node will be accessed externally</li>
                    </ul>
                    <div className="bg-yellow-500/20 border border-yellow-500/40 rounded p-2 mt-3">
                      <p className="text-xs text-yellow-300 flex items-start gap-1">
                        <HelpCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        <span>
                          <span className="font-medium">Tip:</span> Don't have all the information yet? No problem! 
                          Leave any field blank and the generated ENV file will include{' '}
                          <code className="bg-muted px-1 rounded text-yellow-200">&lt;FILL_ME_IN&gt;</code> placeholders 
                          that you can fill in later on your server.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {showPrereqs ? (
            <>
              {/* Prerequisites Section */}
              <div className="space-y-4">
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-md p-4">
                  <h3 className="font-semibold text-purple-300 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-purple-400" />
                    Before You Begin - Prerequisites Checklist
                  </h3>
                  <p className="text-sm text-gray-300 mb-4">
                    Please ensure you have the following ready before bootstrapping your node:
                  </p>

                  <div className="space-y-4">
                    {/* Prereq 1: LLM Compute Infrastructure */}
                    <div className="bg-zinc-900/50 border border-purple-500/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Rocket className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <h4 className="font-semibold text-base text-white">1. LLM Service to Offer</h4>
                          <p className="text-sm text-gray-300">
                            You need access to running AI model(s) that you'll offer to the Morpheus network. Your model must be accessible from the proxy-router via network address (FQDN:port or IP:port).
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">Example endpoint format:</span>{' '}
                            <code className="bg-muted px-1 rounded">http://mymodel.domain.com:5464/v1/chat/completions</code>
                          </p>
                          
                          <Accordion type="single" collapsible className="mt-2">
                            <AccordionItem value="llm-platforms" className="border-blue-500/20">
                              <AccordionTrigger className="text-xs text-blue-200 hover:text-white py-2 hover:no-underline">
                                <span className="flex items-center gap-1">
                                  <HelpCircle className="h-3 w-3" />
                                  Where to host AI models (reference links)
                                </span>
                              </AccordionTrigger>
                              <AccordionContent className="text-xs space-y-2 pt-2">
                                <p className="text-muted-foreground mb-2">Common options (external guides):</p>
                                <ul className="space-y-1.5 text-muted-foreground">
                                  <li className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                    <span>
                                      <span className="font-medium text-foreground">Own Hardware:</span> Run Ollama or llama.cpp locally
                                      <a 
                                        href="https://ollama.ai/" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                      >
                                        Ollama <ExternalLink className="h-2.5 w-2.5" />
                                      </a>
                                      {' / '}
                                      <a 
                                        href="https://github.com/ggerganov/llama.cpp" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline inline-flex items-center gap-0.5"
                                      >
                                        llama.cpp <ExternalLink className="h-2.5 w-2.5" />
                                      </a>
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                    <span>
                                      <span className="font-medium text-foreground">Cloud GPU:</span> RunPod, Vast.ai, or similar
                                      <a 
                                        href="https://www.runpod.io/" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                      >
                                        RunPod <ExternalLink className="h-2.5 w-2.5" />
                                      </a>
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                    <span>
                                      <span className="font-medium text-foreground">Decentralized:</span> Akash Network GPU marketplace
                                      <a 
                                        href="https://akash.network/" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                      >
                                        Akash <ExternalLink className="h-2.5 w-2.5" />
                                      </a>
                                    </span>
                                  </li>
                                </ul>
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mt-2">
                                  <p className="text-xs text-blue-400 flex items-start gap-1">
                                    <HelpCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                    <span>
                                      <span className="font-medium">Note:</span> You'll configure your model details (name, type, endpoint URL) later in the Model Configuration section.
                                    </span>
                                  </p>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      </div>
                    </div>

                    {/* Prereq 2: Wallet */}
                    <div className="bg-zinc-900/50 border border-purple-500/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Wallet className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <h4 className="font-semibold text-base text-white">2. Funded Wallet & Private Key</h4>
                          <p className="text-sm text-gray-300">
                            You need a wallet funded with both ETH (for gas fees) and MOR tokens on the blockchain network you plan to use. 
                          </p>
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
                            <p className="text-xs text-yellow-400 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              <span className="font-medium">Important:</span> Your private key will never leave your machine. It's only used to generate the configuration file locally.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Prereq 3: Network & Host Setup */}
                    <div className="bg-zinc-900/50 border border-purple-500/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <NetworkIcon className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-3">
                          <h4 className="font-semibold text-base text-white">3. Network & Host Configuration</h4>
                          <p className="text-sm text-gray-300">
                            The proxy-router software requires two ports on your host (typically 3333 and 8082):
                          </p>
                          
                          <div className="space-y-3 ml-2">
                            {/* Port 3333 - Critical */}
                            <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertCircle className="h-4 w-4 text-red-400" />
                                <p className="font-medium text-red-400 text-sm">Port 3333 - Proxy Routing (CRITICAL)</p>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">
                                <span className="font-medium text-red-300">Must be publicly accessible from the internet.</span> This is where users connect to use your AI models. 
                                Without internet access to this port, your node cannot serve requests.
                              </p>
                              
                              <Accordion type="single" collapsible className="mt-2">
                                <AccordionItem value="port-3333-help" className="border-red-500/20">
                                  <AccordionTrigger className="text-xs text-red-300 hover:text-red-200 py-2 hover:no-underline">
                                    <span className="flex items-center gap-1">
                                      <HelpCircle className="h-3 w-3" />
                                      How to make port 3333 publicly accessible
                                    </span>
                                  </AccordionTrigger>
                                  <AccordionContent className="text-xs space-y-2 pt-2">
                                    <p className="text-muted-foreground mb-2">Common methods (external guides):</p>
                                    <ul className="space-y-1.5 text-muted-foreground">
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Port Forwarding:</span> Configure your router to forward port 3333
                                          <a 
                                            href="https://portforward.com/" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            Guide <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Cloud Provider:</span> Use security groups/firewall rules (AWS, GCP, Azure)
                                          <a 
                                            href="https://docs.aws.amazon.com/vpc/latest/userguide/vpc-security-groups.html" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            AWS <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Tunneling Service:</span> Use ngrok or similar for testing
                                          <a 
                                            href="https://ngrok.com/docs" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            ngrok <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Reverse Proxy:</span> Nginx/Caddy with public IP
                                          <a 
                                            href="https://docs.nginx.com/nginx/admin-guide/web-server/reverse-proxy/" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            Nginx <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                    </ul>
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>
                            </div>

                            {/* Port 8082 - Important */}
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Server className="h-4 w-4 text-blue-400" />
                                <p className="font-medium text-blue-400 text-sm">Port 8082 - API Management (Recommended HTTPS)</p>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">
                                Admin/management interface for registering providers, models, and bids. Ideally accessible via HTTPS from the internet 
                                (protected by your admin password), but can be local network only if needed.
                              </p>
                              <p className="text-xs text-muted-foreground mb-2">
                                <span className="font-medium text-blue-300">Public URL Examples:</span><br/>
                                • With TLS/Load Balancer: <code className="bg-muted px-1 rounded">https://proxyapi.yourhost.com</code><br/>
                                • Direct: <code className="bg-muted px-1 rounded">http://your-ip:8082</code> or <code className="bg-muted px-1 rounded">http://hostname:8082</code>
                              </p>
                              
                              <Accordion type="single" collapsible className="mt-2">
                                <AccordionItem value="port-8082-help" className="border-blue-500/20">
                                  <AccordionTrigger className="text-xs text-blue-300 hover:text-blue-200 py-2 hover:no-underline">
                                    <span className="flex items-center gap-1">
                                      <HelpCircle className="h-3 w-3" />
                                      How to setup HTTPS/TLS for port 8082
                                    </span>
                                  </AccordionTrigger>
                                  <AccordionContent className="text-xs space-y-2 pt-2">
                                    <p className="text-muted-foreground mb-2">Common HTTPS setup methods (external guides):</p>
                                    <ul className="space-y-1.5 text-muted-foreground">
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Let's Encrypt + Nginx:</span> Free SSL certificates with reverse proxy
                                          <a 
                                            href="https://certbot.eff.org/instructions" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            Certbot <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Caddy Server:</span> Automatic HTTPS with zero configuration
                                          <a 
                                            href="https://caddyserver.com/docs/automatic-https" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            Caddy <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Cloud Load Balancer:</span> AWS ALB, GCP Load Balancer, Cloudflare
                                          <a 
                                            href="https://docs.aws.amazon.com/elasticloadbalancing/latest/application/create-https-listener.html" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            AWS <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <ChevronRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>
                                          <span className="font-medium text-foreground">Home Router + DuckDNS:</span> Dynamic DNS + port 443 forwarding
                                          <a 
                                            href="https://www.duckdns.org/" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:underline ml-1 inline-flex items-center gap-0.5"
                                          >
                                            DuckDNS <ExternalLink className="h-2.5 w-2.5" />
                                          </a>
                                        </span>
                                      </li>
                                    </ul>
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
                                      <p className="text-xs text-yellow-400 flex items-start gap-1">
                                        <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                        <span>
                                          <span className="font-medium">Best with dashboard:</span> HTTPS endpoints work best with{' '}
                                          <code className="bg-muted px-1 rounded">https://myprovider.mor.org</code>
                                        </span>
                                      </p>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>
                            </div>
                          </div>

                          <div className="border-t border-border/20 pt-3 mt-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Server className="h-4 w-4 text-purple-400" />
                              <p className="font-medium text-sm text-foreground">Host Software</p>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              Your server needs one of the following to run the proxy-router:
                            </p>
                            <ul className="text-xs text-muted-foreground space-y-1.5 ml-2">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-400 flex-shrink-0">•</span>
                                <span><span className="font-medium text-foreground">Docker</span> - Container runtime (recommended for easy setup)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-400 flex-shrink-0">•</span>
                                <span><span className="font-medium text-foreground">Direct Binary</span> - Ability to download and run the proxy-router binary</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={() => setShowPrereqs(false)} 
                    className="flex-1"
                    size="lg"
                  >
                    I'm Ready - Continue to Setup
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                    size="lg"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          ) : !showEnvFile ? (
            <>
              {/* Configuration Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Chain</Label>
                    <Select
                      value={chain}
                      onValueChange={(value) => setChain(value as Chain)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="arbitrum">⚡ Arbitrum</SelectItem>
                        <SelectItem value="base">🔵 Base</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Network</Label>
                    <Select
                      value={network}
                      onValueChange={(value) => setNetwork(value as Network)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mainnet">🟢 Mainnet (Production)</SelectItem>
                        <SelectItem value="testnet">🟡 Testnet (Development)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wallet-private-key">Wallet Private Key *</Label>
                  <Input
                    id="wallet-private-key"
                    name="wallet-private-key"
                    type="password"
                    placeholder="0x..."
                    value={walletPrivateKey}
                    onChange={(e) => setWalletPrivateKey(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your wallet's private key. Keep this secure and never share it.
                  </p>
                  <p className="text-xs text-blue-400 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Session only — used locally to generate the ENV file; not uploaded
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eth-node-address">ETH_NODE_ADDRESS (Base RPC)</Label>
                  <Input
                    id="eth-node-address"
                    type="text"
                    placeholder="https://base-mainnet.g.alchemy.com/v2/…"
                    value={ethNodeAddress}
                    onChange={(e) => setEthNodeAddress(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Required for real providers. SecretVM uses this as one of the 5 encrypted secrets.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Proxy Routing Port</Label>
                    <Input
                      type="text"
                      placeholder="3333"
                      value={proxyPort}
                      onChange={(e) => setProxyPort(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Port where users connect to your AI models (must be publicly accessible)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>API Management Port</Label>
                    <Input
                      type="text"
                      placeholder="8082"
                      value={apiPort}
                      onChange={(e) => setApiPort(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Port for admin/management interface
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Public API URL *</Label>
                  <Input
                    type="text"
                    placeholder="proxyapi.example.com:8082 or https://proxyapi.example.com"
                    value={webPublicUrl}
                    onChange={(e) => setWebPublicUrl(e.target.value)}
                  />
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      The public URL where your API management interface can be reached. This is what you'll use to connect this app to your node.
                    </p>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
                      <p className="text-xs text-blue-400">
                        <span className="font-medium">Examples:</span><br/>
                        • With HTTPS/reverse proxy: <code className="bg-muted px-1 rounded">https://proxyapi.yourhost.com</code><br/>
                        • Direct (with port): <code className="bg-muted px-1 rounded">proxyapi.yourhost.com:8082</code> or <code className="bg-muted px-1 rounded">123.45.67.89:8082</code><br/>
                        <br/>
                        <span className="font-medium">Note:</span> Don't include "http://" in the value - just the hostname/IP and optional port.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin-password">Admin Password *</Label>
                  <Input
                    id="admin-password"
                    name="admin-password"
                    type="password"
                    placeholder="Enter a secure password for API access"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-muted-foreground">
                    This password will be used to connect to your node's API (username is always "admin")
                  </p>
                  <p className="text-xs text-blue-400 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    This information stays on your machine - it's only used to generate the ENV file locally
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowPrereqs(true)}
                  className="flex-1"
                  type="button"
                >
                  ← Back to Prerequisites
                </Button>
                <Button 
                  onClick={generateEnvFile} 
                  className="flex-1" 
                  size="lg"
                  type="button"
                >
                  Next: Generate ENV File
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* ENV File Display */}
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4">
                  <p className="text-sm text-green-400 font-semibold">
                    ✓ Configuration Generated!
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Follow the setup instructions below to get started.
                  </p>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => setIsEnvExpanded(!isEnvExpanded)}
                    className="w-full flex items-center justify-between p-3 bg-muted/30 border border-border rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <Label className="cursor-pointer">ENV File Content <span className="text-xs text-muted-foreground">(click to edit)</span></Label>
                    <ChevronDown 
                      className={`h-4 w-4 transition-transform ${isEnvExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isEnvExpanded && (
                    <textarea
                      value={editableEnvContent}
                      onChange={(e) => setEditableEnvContent(e.target.value)}
                      className="w-full bg-muted/30 border border-border rounded-md p-4 text-xs font-mono whitespace-pre overflow-y-auto resize-y min-h-[200px] max-h-96 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      spellCheck={false}
                    />
                  )}
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-4">
                  <p className="font-semibold text-blue-400 mb-3">Setup Instructions:</p>
                  
                  <Tabs value={setupTab} onValueChange={(v) => setSetupTab(v as typeof setupTab)} className="w-full">
                    <TabsList className="w-full mb-4 border-b border-zinc-700 flex flex-wrap h-auto">
                      <TabsTrigger value="secretvm">SecretVM</TabsTrigger>
                      <TabsTrigger value="docker">Container</TabsTrigger>
                      <TabsTrigger value="binary">Release binary</TabsTrigger>
                      <TabsTrigger value="github">GitHub</TabsTrigger>
                    </TabsList>

                    <TabsContent value="secretvm" className="space-y-3">
                      <ol className="list-decimal space-y-3 text-sm text-foreground">
                        <li className="ml-5">
                          <span className="font-medium">Copy the 5 SecretVM secrets</span> from the generated ENV
                          (or from onboarding step 3). Prefer the digest-pinned{' '}
                          <code className="bg-muted px-1 rounded">docker-compose.tee.deployed.yml</code> from{' '}
                          <a href={EXTERNAL_LINKS.releases} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                            Releases
                          </a>
                          .
                        </li>
                        <li className="ml-5">
                          Create a VM at{' '}
                          <a href={EXTERNAL_LINKS.secretVmPortal} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                            SecretVM portal
                          </a>
                          . Paste compose + secrets. TEE image is optional but recommended for attestation.
                        </li>
                        <li className="ml-5">
                          Verify <code className="bg-muted px-1 rounded">curl https://&lt;vm&gt;/healthcheck</code>, then
                          connect MyProvider to that HTTPS URL with COOKIE_CONTENT credentials.
                        </li>
                        <li className="ml-5">
                          Register provider + <strong>bid on an existing model</strong> from active.mor.org (do not mint duplicates).
                        </li>
                      </ol>
                      <div className="flex gap-2">
                        <Button onClick={handleDownloadEnv} size="sm">
                          <Download className="h-4 w-4 mr-1" /> Download secrets file
                        </Button>
                        <Button variant="outline" onClick={handleCopyEnv} size="sm">
                          <Copy className="h-4 w-4 mr-1" /> Copy
                        </Button>
                      </div>
                    </TabsContent>

                    <TabsContent value="github" className="space-y-3">
                      <ol className="list-decimal space-y-3 text-sm text-foreground">
                        <li className="ml-5">
                          <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto mt-1">{`git clone https://github.com/MorpheusAIs/Morpheus-Lumerin-Node.git
cd Morpheus-Lumerin-Node/proxy-router
./build.sh`}</pre>
                        </li>
                        <li className="ml-5">
                          Save the ENV content as <code className="bg-muted px-1 rounded">.env</code> in the working directory
                          (download below), then run the built binary.
                        </li>
                        <li className="ml-5">
                          Look up models on active.mor.org, register via MyProvider, bid on an existing Id.
                        </li>
                      </ol>
                      <div className="flex gap-2">
                        <Button onClick={handleDownloadEnv} size="sm">
                          <Download className="h-4 w-4 mr-1" /> Download .env
                        </Button>
                        <Button variant="outline" onClick={handleCopyEnv} size="sm">
                          <Copy className="h-4 w-4 mr-1" /> Copy
                        </Button>
                        <a
                          href="https://github.com/MorpheusAIs/Morpheus-Lumerin-Node"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-xs text-blue-400 hover:underline px-2"
                        >
                          Repo <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </TabsContent>

                    <TabsContent value="docker" className="space-y-3">
                      <ol className="list-decimal space-y-3 text-sm text-foreground">
                        <li className="ml-5">
                          <span className="font-medium">Save the ENV content to a file named .env</span>
                          <div className="flex gap-2 mt-2">
                            <Button
                              onClick={handleDownloadEnv}
                              className="flex items-center gap-2"
                              size="sm"
                            >
                              <Download className="h-4 w-4" />
                              Download .env File
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCopyEnv}
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Copy className="h-4 w-4" />
                              Copy to Clipboard
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Pull the latest Docker image:</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto">docker pull ghcr.io/morpheusais/morpheus-lumerin-node:latest</pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                navigator.clipboard.writeText('docker pull ghcr.io/morpheusais/morpheus-lumerin-node:latest');
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Run the container with your ENV file:</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">{`docker run -d \\
  --name morpheus-proxy-router \\
  --env-file .env \\
  -p ${proxyPort}:${proxyPort} \\
  -p ${apiPort}:${apiPort} \\
  ghcr.io/morpheusais/morpheus-lumerin-node:latest`}</pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                const cmd = `docker run -d \\
  --name morpheus-proxy-router \\
  --env-file .env \\
  -p ${proxyPort}:${proxyPort} \\
  -p ${apiPort}:${apiPort} \\
  ghcr.io/morpheusais/morpheus-lumerin-node:latest`;
                                navigator.clipboard.writeText(cmd);
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Verify the node is running:</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto">{`curl http://localhost:${apiPort}/healthcheck`}</pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                navigator.clipboard.writeText(`curl http://localhost:${apiPort}/healthcheck`);
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Once healthy, connect using the API Configuration section above!</span>
                        </li>
                      </ol>
                    </TabsContent>

                    <TabsContent value="binary" className="space-y-3">
                      {detectedOS !== 'unknown' && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 mb-3">
                          <p className="text-xs text-emerald-400 flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            <span>
                              <span className="font-medium">Detected:</span> {getPlatformName(binaryPlatform)}
                              {' '}- You can change the platform below if this is incorrect
                            </span>
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-blue-400 mb-2 block">Select Platform:</Label>
                          <Select value={binaryPlatform} onValueChange={setBinaryPlatform}>
                            <SelectTrigger className="bg-muted/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="linux-x86_64">Linux (x86_64)</SelectItem>
                              <SelectItem value="darwin-aarch64">macOS (Apple Silicon)</SelectItem>
                              <SelectItem value="darwin-x86_64">macOS (Intel)</SelectItem>
                              <SelectItem value="windows-x86_64">Windows (x64)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-blue-400 mb-2 block">Select Version:</Label>
                          <Select value={selectedVersion} onValueChange={setSelectedVersion} disabled={isLoadingReleases}>
                            <SelectTrigger className="bg-muted/50">
                              <SelectValue>
                                {isLoadingReleases ? 'Loading versions...' : selectedVersion}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {availableReleases.map((release) => (
                                <SelectItem key={release.tag_name} value={release.tag_name}>
                                  {release.tag_name}
                                  {release.tag_name.includes('-test') ? ' (Test)' : ' (Stable)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <ol className="list-decimal space-y-3 text-sm text-foreground">
                        <li className="ml-5">
                          <span className="font-medium">Create a directory and navigate to it:</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">{`mkdir -p ~/morpheus-node
cd ~/morpheus-node`}</pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                navigator.clipboard.writeText(`mkdir -p ~/morpheus-node\ncd ~/morpheus-node`);
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Save the ENV content to a file named .env:</span>
                          <div className="flex gap-2 mt-2">
                            <Button
                              onClick={handleDownloadEnv}
                              className="flex items-center gap-2"
                              size="sm"
                            >
                              <Download className="h-4 w-4" />
                              Download .env File
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCopyEnv}
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Copy className="h-4 w-4" />
                              Copy to Clipboard
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {detectedOS === 'windows' 
                              ? 'Save the downloaded file to your project directory (e.g., C:\\morpheus-node\\.env)'
                              : 'Save the downloaded file to ~/morpheus-node/.env or use the command line to create it'}
                          </p>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Download the proxy-router binary for {getPlatformName(binaryPlatform)}:</span>
                          <div className="flex gap-2 mt-2">
                            <Button
                              onClick={handleDownloadBinary}
                              className="flex items-center gap-2"
                              size="sm"
                            >
                              <Download className="h-4 w-4" />
                              Download Binary ({selectedVersion})
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(getBinaryDownloadUrl(binaryPlatform, selectedVersion));
                                success('Copied!', 'Download URL copied to clipboard');
                              }}
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Copy className="h-4 w-4" />
                              Copy URL
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => window.open(`https://github.com/MorpheusAIs/Morpheus-Lumerin-Node/releases/tag/${selectedVersion}`, '_blank')}
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              View Release
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {detectedOS === 'macos' && 'After downloading, move the binary to ~/morpheus-node/ and rename it to proxy-router'}
                            {detectedOS === 'linux' && 'After downloading, move the binary to ~/morpheus-node/ and rename it to proxy-router'}
                            {detectedOS === 'windows' && 'After downloading, move the binary to your project folder and rename it to proxy-router.exe'}
                            {detectedOS === 'unknown' && 'After downloading, place the binary in your project directory'}
                          </p>
                        </li>
                        {(detectedOS === 'macos' || detectedOS === 'linux' || detectedOS === 'unknown') && (
                          <li className="ml-5">
                            <span className="font-medium">Make the binary executable:</span>
                            <div className="relative group mt-2">
                              <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">chmod +x proxy-router</pre>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => {
                                  navigator.clipboard.writeText('chmod +x proxy-router');
                                  success('Copied!', 'Command copied to clipboard');
                                }}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            {detectedOS === 'macos' && (
                              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
                                <p className="text-xs text-yellow-400 flex items-start gap-1">
                                  <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                  <span>
                                    <span className="font-medium">macOS Security:</span> If you see "cannot be opened because it is from an unidentified developer", 
                                    run: <code className="bg-muted px-1 rounded whitespace-nowrap">xattr -cr proxy-router</code> then try again.
                                  </span>
                                </p>
                              </div>
                            )}
                          </li>
                        )}
                        <li className="ml-5">
                          <span className="font-medium">Run the proxy-router (it will automatically load the .env file):</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">
                              {detectedOS === 'windows' ? '.\\proxy-router.exe' : './proxy-router'}
                            </pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                const cmd = detectedOS === 'windows' ? '.\\proxy-router.exe' : './proxy-router';
                                navigator.clipboard.writeText(cmd);
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
                            <p className="text-xs text-yellow-400 flex items-start gap-1">
                              <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                              <span>
                                <span className="font-medium">Note:</span> The process will run in the foreground. 
                                {detectedOS === 'windows' && ' To run in the background, use Windows Task Scheduler or run as a Windows Service.'}
                                {(detectedOS === 'macos' || detectedOS === 'linux' || detectedOS === 'unknown') && 
                                  <> To run it in the background, use <code className="bg-muted px-1 rounded whitespace-nowrap">nohup ./proxy-router &</code> 
                                  or set it up as a system service.</>
                                }
                              </span>
                            </p>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Verify the node is running (in a new terminal):</span>
                          <div className="relative group mt-2">
                            <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">{`curl http://localhost:${apiPort}/healthcheck`}</pre>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                navigator.clipboard.writeText(`curl http://localhost:${apiPort}/healthcheck`);
                                success('Copied!', 'Command copied to clipboard');
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </li>
                        <li className="ml-5">
                          <span className="font-medium">Once healthy, connect using the API Configuration section above!</span>
                        </li>
                      </ol>
                    </TabsContent>
                  </Tabs>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEnvFile(false);
                      setShowPrereqs(true);
                    }}
                    className="flex-1"
                  >
                    Start Over
                  </Button>
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      setShowPrereqs(true);
                      setShowEnvFile(false);
                    }}
                    className="flex-1"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

